/** PDF coordinates remain in source user space. Viewport coordinates are CSS pixels,
 * not canvas backing pixels. Matrices follow PDF.js PageViewport's default y flip. */
export interface PdfRect { x: number; y: number; width: number; height: number }
export interface PdfPageGeometry {
  pageIndex: number;
  mediaBox: PdfRect;
  cropBox: PdfRect;
  visibleBox: PdfRect;
  rotation: 0 | 90 | 180 | 270;
  userUnit: number;
}
export type PdfMatrix = readonly [number, number, number, number, number, number];
export interface PdfViewport { width: number; height: number; transform: PdfMatrix }
export type PdfPoint = readonly [number, number];

export function validRect(rect: PdfRect): boolean {
  return [rect.x, rect.y, rect.width, rect.height, rect.x + rect.width, rect.y + rect.height].every(Number.isFinite) && rect.width > 0 && rect.height > 0;
}
export function intersectBoxes(a: PdfRect, b: PdfRect): PdfRect | undefined {
  const x = Math.max(a.x, b.x), y = Math.max(a.y, b.y);
  const result = { x, y, width: Math.min(a.x + a.width, b.x + b.width) - x, height: Math.min(a.y + a.height, b.y + b.height) - y };
  return validRect(result) ? result : undefined;
}
export function pageViewport(page: PdfPageGeometry, scale = 1): PdfViewport {
  if (!Number.isFinite(scale) || scale <= 0 || !validRect(page.visibleBox) || !Number.isFinite(page.userUnit) || page.userUnit <= 0) throw new Error('Invalid PDF viewport');
  const { x, y, width: w, height: h } = page.visibleBox;
  const s = scale * page.userUnit;
  let transform: PdfMatrix;
  switch (page.rotation) {
    case 0: transform = [s, 0, 0, -s, -x * s, (y + h) * s]; break;
    case 90: transform = [0, s, s, 0, -y * s, -x * s]; break;
    case 180: transform = [-s, 0, 0, s, (x + w) * s, -y * s]; break;
    case 270: transform = [0, -s, -s, 0, (y + h) * s, (x + w) * s]; break;
    default: throw new Error('Unsupported PDF rotation');
  }
  const swapped = page.rotation === 90 || page.rotation === 270;
  const width = (swapped ? h : w) * s, height = (swapped ? w : h) * s;
  if (![width, height, ...transform].every(Number.isFinite)) throw new Error('Invalid PDF viewport');
  return { width, height, transform };
}
export function transformPoint(matrix: PdfMatrix, [x, y]: PdfPoint): PdfPoint {
  const [a, b, c, d, e, f] = matrix;
  return [a * x + c * y + e, b * x + d * y + f];
}
export function inverseMatrix(matrix: PdfMatrix): PdfMatrix {
  const [a, b, c, d, e, f] = matrix, determinant = a * d - b * c;
  if (!matrix.every(Number.isFinite) || !Number.isFinite(determinant) || determinant === 0) throw new Error('Invalid PDF transform');
  return [d / determinant, -b / determinant, -c / determinant, a / determinant, (c * f - d * e) / determinant, (b * e - a * f) / determinant];
}
export function rectCorners(rect: PdfRect): PdfPoint[] {
  return [[rect.x, rect.y], [rect.x + rect.width, rect.y], [rect.x + rect.width, rect.y + rect.height], [rect.x, rect.y + rect.height]];
}
export function transformRect(matrix: PdfMatrix, rect: PdfRect): PdfRect {
  if (!validRect(rect)) throw new Error('Invalid PDF rectangle');
  const points = rectCorners(rect).map(point => transformPoint(matrix, point));
  const x = Math.min(...points.map(point => point[0])), y = Math.min(...points.map(point => point[1]));
  return { x, y, width: Math.max(...points.map(point => point[0])) - x, height: Math.max(...points.map(point => point[1])) - y };
}
export function pdfRectToViewport(viewport: PdfViewport, rect: PdfRect): PdfRect { return transformRect(viewport.transform, rect); }
export function viewportRectToPdf(viewport: PdfViewport, rect: PdfRect): PdfRect { return transformRect(inverseMatrix(viewport.transform), rect); }
export function placementBoundsError(pages: PdfPageGeometry[], pageIndex: number, rect: PdfRect): string | undefined {
  if (!Number.isSafeInteger(pageIndex) || pageIndex < 0 || !pages[pageIndex]) return 'Choose an existing PDF page.';
  if (!validRect(rect)) return 'Use a finite rectangle with positive width and height.';
  const box = pages[pageIndex].visibleBox, tolerance = 0.001;
  if (rect.x < box.x - tolerance || rect.y < box.y - tolerance || rect.x + rect.width > box.x + box.width + tolerance || rect.y + rect.height > box.y + box.height + tolerance) return 'Place the entire field inside the visible PDF page.';
}
